"use client";
import { jsx } from "react/jsx-runtime";
import { createContext, useContext } from "react";
import * as MakeswiftApiClient from "../state/makeswift-api-client";
import {
  APIResourceType
} from "./graphql/types";
import { GraphQLClient } from "./graphql/client";
import { CreateTableRecordMutation } from "./graphql/documents";
class MakeswiftClient {
  graphqlClient;
  makeswiftApiClient;
  subscribe;
  localizedResourcesMap;
  locale;
  constructor({ uri, cacheData, localizedResourcesMap = {}, locale }) {
    this.graphqlClient = new GraphQLClient(uri);
    this.makeswiftApiClient = MakeswiftApiClient.configureStore({ serializedState: cacheData });
    this.subscribe = this.makeswiftApiClient.subscribe;
    this.localizedResourcesMap = new Map(Object.entries(localizedResourcesMap));
    this.locale = locale;
  }
  readSwatch(swatchId) {
    return MakeswiftApiClient.getAPIResource(
      this.makeswiftApiClient.getState(),
      APIResourceType.Swatch,
      swatchId
    );
  }
  async fetchSwatch(swatchId) {
    return await this.makeswiftApiClient.dispatch(
      MakeswiftApiClient.fetchAPIResource(APIResourceType.Swatch, swatchId)
    );
  }
  readFile(fileId) {
    return MakeswiftApiClient.getAPIResource(
      this.makeswiftApiClient.getState(),
      APIResourceType.File,
      fileId
    );
  }
  async fetchFile(fileId) {
    return await this.makeswiftApiClient.dispatch(
      MakeswiftApiClient.fetchAPIResource(APIResourceType.File, fileId)
    );
  }
  readTypography(typographyId) {
    return MakeswiftApiClient.getAPIResource(
      this.makeswiftApiClient.getState(),
      APIResourceType.Typography,
      typographyId
    );
  }
  async fetchTypography(typographyId) {
    return await this.makeswiftApiClient.dispatch(
      MakeswiftApiClient.fetchAPIResource(APIResourceType.Typography, typographyId)
    );
  }
  readGlobalElement(globalElementId) {
    return MakeswiftApiClient.getAPIResource(
      this.makeswiftApiClient.getState(),
      APIResourceType.GlobalElement,
      globalElementId
    );
  }
  async fetchGlobalElement(globalElementId) {
    return await this.makeswiftApiClient.dispatch(
      MakeswiftApiClient.fetchAPIResource(APIResourceType.GlobalElement, globalElementId)
    );
  }
  readLocalizedGlobalElement(globalElementId) {
    const localizedGlobalElementId = this.getLocalizedResourceId(globalElementId);
    if (localizedGlobalElementId == null)
      return null;
    return MakeswiftApiClient.getAPIResource(
      this.makeswiftApiClient.getState(),
      APIResourceType.LocalizedGlobalElement,
      localizedGlobalElementId
    );
  }
  async fetchLocalizedGlobalElement(globalElementId) {
    const locale = this.locale;
    if (locale == null)
      return null;
    const noLocalizedResource = this.getLocalizedResourceId(globalElementId) === null;
    if (noLocalizedResource)
      return null;
    const cacheResult = this.readLocalizedGlobalElement(globalElementId);
    if (cacheResult)
      return cacheResult;
    const result = await this.makeswiftApiClient.dispatch(
      MakeswiftApiClient.fetchAPIResource(
        APIResourceType.LocalizedGlobalElement,
        globalElementId,
        locale
      )
    );
    this.setLocalizedResourceId({
      resourceId: globalElementId,
      localizedResourceId: result?.id ?? null
    });
    return result;
  }
  readPagePathnameSlice(pageId) {
    return MakeswiftApiClient.getAPIResource(
      this.makeswiftApiClient.getState(),
      APIResourceType.PagePathnameSlice,
      pageId
    );
  }
  async fetchPagePathnameSlice(pageId) {
    return await this.makeswiftApiClient.dispatch(
      MakeswiftApiClient.fetchAPIResource(APIResourceType.PagePathnameSlice, pageId, this.locale)
    );
  }
  readTable(tableId) {
    return MakeswiftApiClient.getAPIResource(
      this.makeswiftApiClient.getState(),
      APIResourceType.Table,
      tableId
    );
  }
  async fetchTable(tableId) {
    return await this.makeswiftApiClient.dispatch(
      MakeswiftApiClient.fetchAPIResource(APIResourceType.Table, tableId)
    );
  }
  async createTableRecord(tableId, columns) {
    await this.graphqlClient.request(CreateTableRecordMutation, { input: { data: { tableId, columns } } });
  }
  readSite(siteId) {
    return MakeswiftApiClient.getAPIResource(
      this.makeswiftApiClient.getState(),
      APIResourceType.Site,
      siteId
    );
  }
  readPage(pageId) {
    return MakeswiftApiClient.getAPIResource(
      this.makeswiftApiClient.getState(),
      APIResourceType.Page,
      pageId
    );
  }
  readSnippet(snippetId) {
    return MakeswiftApiClient.getAPIResource(
      this.makeswiftApiClient.getState(),
      APIResourceType.Snippet,
      snippetId
    );
  }
  getLocalizedResourceId(resourceId) {
    return this.localizedResourcesMap?.get(resourceId);
  }
  setLocalizedResourceId({
    resourceId,
    localizedResourceId
  }) {
    this.localizedResourcesMap.set(resourceId, localizedResourceId);
  }
}
const Context = createContext(new MakeswiftClient({ uri: "https://api.makeswift.com/graphql" }));
function useMakeswiftClient() {
  return useContext(Context);
}
function MakeswiftProvider({ client, children }) {
  return /* @__PURE__ */ jsx(Context.Provider, { value: client, children });
}
export {
  MakeswiftClient,
  MakeswiftProvider,
  useMakeswiftClient
};
//# sourceMappingURL=react.js.map